import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Users as UsersIcon, Download } from 'lucide-react';
import { Pagination, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge, Button, Checkbox } from '@/components/shared/ui';
import { useUserSelection } from '@/pages/users/hooks/useUserSelection';
import EditUserModal from '../users/modals/EditUserModal';
import AddMemberModal, { type MockMember } from '../users/modals/AddMemberModal';
import CreateGuestModal, { type CreateGuestSaveContext } from '../users/modals/CreateGuestModal';
import { User, License } from '../../types/admin';
import { formatDateTime } from '@/utils/dateUtils';
import type { OrganizationUserInfoDto } from '../../types/api/user.d';
import {
    formatUsersPermissionLabel,
    getUserPermissionKind,
    type UserPermissionKind,
} from '@/pages/users/utils/userManagementUtils';
import { cn } from '@/lib/cn';
import { resolveOrganizationUserAccountKind, organizationUserListRoleLabel } from '@/utils/roleUtils';
import { JobTitle } from '@/types/api/auth';
import type { TFunction } from 'i18next';
import { apiValueToDisplayString } from '@/utils/apiDisplayString';

/** 목록 DTO에서 표시 문자열 추출 — 문자열·중첩/다국어 객체·camel·snake 별칭 병행 */
function coalesceOrgListDisplayField(...vals: unknown[]): string | undefined {
    for (const v of vals) {
        if (v === null || v === undefined) continue;
        if (typeof v === 'string') {
            const t = v.trim();
            if (t) return t;
            continue;
        }
        const s = apiValueToDisplayString(v).trim();
        if (s) return s;
    }
    return undefined;
}

const JOB_TITLE_SETTINGS_I18N_KEY: Partial<Record<JobTitle, string>> = {
    [JobTitle.DOCTOR]: 'doctor',
    [JobTitle.NURSE]: 'nurse',
    [JobTitle.NURSING_AIDE]: 'nurseAide',
    [JobTitle.PARAMEDIC]: 'paramedic',
    [JobTitle.EDUCATOR]: 'educator',
    [JobTitle.STUDENT]: 'student',
    [JobTitle.RESEARCHER]: 'researcher',
    [JobTitle.CUSTOM]: 'jobRoleCustom',
};

function userListPermissionBadgeClass(kind: UserPermissionKind): {
    variant: 'default' | 'outline' | 'secondary' | 'statusActive';
    className?: string;
} {
    switch (kind) {
        case 'guest':
            return {
                variant: 'outline',
                className:
                    'border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-50 focus:ring-slate-400/30',
            };
        case 'regular_member':
            return {
                variant: 'outline',
                className:
                    'border-green-200 bg-green-50 text-green-800 hover:bg-green-50 focus:ring-green-500/25',
            };
        case 'master':
            return { variant: 'default', className: undefined };
        case 'admin':
            return {
                variant: 'outline',
                className:
                    'border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-50 focus:ring-indigo-500/25',
            };
        default:
            return { variant: 'outline', className: 'border-gray-200 text-gray-700' };
    }
}

function formatUserListJobTitle(user: User, tAuth: TFunction<'auth'>): string {
    const jt = user.jobTitle?.trim();
    const custom = user.customJobTitle?.trim();
    if (!jt) return '-';
    if (jt === JobTitle.CUSTOM || jt === 'CUSTOM') return custom || '-';
    const settingsKey = JOB_TITLE_SETTINGS_I18N_KEY[jt as JobTitle];
    if (settingsKey) return tAuth(`settings.${settingsKey}`);
    return custom || jt;
}

interface UserListProps {
    users: User[];
    licenses?: License[];
    organizationId?: number | null;
    /** 기관명 - 프로덕트 카드용 주문내역 필터(기관주문 ordererName) */
    organizationName?: string;
    showStats?: boolean;
    onAddMember?: (newMembers: Partial<User>[]) => void;
    onCreateGuest?: (newGuests: Partial<User>[], context: CreateGuestSaveContext) => void | Promise<void>;
    onSaveUser?: (user: User) => void | Promise<void>;
    /** 선택 삭제 확정 시 호출 (삭제 대상 사용자 목록 전달) */
    onDeleteSelectedUsers?: (users: User[]) => void;
    /**
     * true: 라이선스 목록 API를 쓰지 않을 때(엔드포인트 미제공·404 등). 인원상한을 알 수 없어 등록가능 수를 ‘제한 없음’으로 보고
     * `validateLicenseLimit`은 통과시킴.
     */
    omitLicenseQuotaCheck?: boolean;
    /** 백엔드 페이지네이션 */
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    /** 백엔드 통계 */
    masterUserCount?: number;
    studentUserCount?: number;
}

/** 참여 클래스명 정규화(공백 제거·빈 항목 제외) */
function resolveClassCountDisplay(user: User): number | string {
    if (user.classCount != null && Number.isFinite(Number(user.classCount))) {
        return user.classCount;
    }
    return '-';
}

/**
 * OrganizationUserInfoDto를 User 타입으로 변환 (mock 데이터 적용 시 외부에서 사용)
 */
export const convertOrganizationUserInfoDtoToUser = (dto: OrganizationUserInfoDto): User => {
    const accountKind = resolveOrganizationUserAccountKind(dto);
    const perm = organizationUserListRoleLabel(dto);
    const roleStr =
        perm === 'Guest' ? 'Guest' : perm === 'Master' ? 'Master' : perm === 'Admin' ? 'Admin' : 'Student';
    const raw = dto as OrganizationUserInfoDto & Record<string, unknown>;
    const profile =
        raw.profile && typeof raw.profile === 'object'
            ? (raw.profile as Record<string, unknown>)
            : raw.userProfile && typeof raw.userProfile === 'object'
              ? (raw.userProfile as Record<string, unknown>)
              : null;
    const affiliation = coalesceOrgListDisplayField(
        dto.affiliation,
        raw.affiliation,
        raw.organization_affiliation,
        raw.organizationAffiliation,
        raw.org_affiliation,
        raw.user_affiliation,
        profile?.affiliation,
        profile?.organization_affiliation
    );
    const position = coalesceOrgListDisplayField(
        dto.position,
        raw.position,
        raw.position_title,
        raw.positionTitle,
        raw.job_position,
        raw.jobPosition,
        profile?.position,
        profile?.position_title
    );
    const jobTitle = coalesceOrgListDisplayField(
        dto.jobTitle,
        raw.job_title,
        raw.jobDuty,
        raw.job_duty,
        raw.job_role,
        raw.jobRole,
        profile?.job_title,
        profile?.jobTitle
    );
    const customJobTitle = coalesceOrgListDisplayField(
        dto.customJobTitle,
        raw.custom_job_title,
        raw.customJobTitle,
        profile?.custom_job_title,
        profile?.customJobTitle
    );
    const department = coalesceOrgListDisplayField(
        dto.department,
        raw.department,
        raw.dept,
        raw.dept_name,
        raw.deptName,
        raw.department_name,
        raw.departmentName,
        profile?.department,
        profile?.dept_name
    );
    return {
        id: dto.userId.toString(),
        name: dto.name,
        email: dto.email,
        type: accountKind === 'REGULAR' ? 'regular' : 'guest',
        accountType: accountKind === 'REGULAR' ? '정회원' : '게스트',
        customerType: '기관고객',
        role: roleStr,
        registeredDate: dto.createdAt,
        lastLogin: dto.lastSigninAt,
        status: dto.isActive ? 'active' : 'inactive',
        loginId: dto.loginId, // 엄격하게: 아이디 필드 추가
        productName: dto.productName,
        participatingClass: dto.participatingClass,
        simulationCount: dto.scenarioCount,
        averageCompletionRate: dto.averageCompletionRate,
        affiliation,
        position,
        jobTitle,
        customJobTitle,
        ...(department ? { department } : {}),
    };
};

export default function UserList({
    users: initialUsers,
    licenses = [],
    organizationId = null,
    organizationName,
    showStats = true,
    onAddMember,
    onCreateGuest,
    onSaveUser,
    onDeleteSelectedUsers,
    omitLicenseQuotaCheck = false,
    currentPage: externalCurrentPage,
    totalPages: externalTotalPages,
    totalCount: externalTotalCount,
    onPageChange: externalOnPageChange,
    masterUserCount: externalMasterUserCount,
    studentUserCount: externalStudentUserCount,
}: UserListProps): React.ReactElement {
    const { t, i18n } = useTranslation('admin');
    const { t: tAuth } = useTranslation('auth');
    const { t: tUsers } = useTranslation('users');
    const profileLocale = i18n.resolvedLanguage || i18n.language;

    const [users, setUsers] = useState<User[]>(initialUsers);

    useEffect(() => {
        setUsers(initialUsers);
    }, [initialUsers]);

    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState<boolean>(false);
    const [isCreateGuestModalOpen, setIsCreateGuestModalOpen] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Pagination - 백엔드 페이지네이션만 사용
    const currentPage = externalCurrentPage ?? 1;
    const setCurrentPage = externalOnPageChange ?? (() => {});
    const itemsPerPage = 20;

    const displayUsers = users;

    // Calculate stats - 백엔드 통계 사용
    const userStats = {
        total: externalTotalCount ?? 0,
        masters: externalMasterUserCount ?? 0,
        regularMembers: externalStudentUserCount ?? 0,
        active: displayUsers.filter((u) => u.status === 'active').length,
    };

    // Calculate license limits
    const licenseInfo = useMemo(() => {
        if (omitLicenseQuotaCheck) {
            return {
                hasDeviceLicense: true,
                maxUsers: null,
                currentUsers: displayUsers.filter((u) => u.status === 'active').length,
            };
        }
        // Check if there's any device license (unlimited users)
        const hasDeviceLicense = licenses.some(license =>
            (license.licenseType === '기기구독' || license.subscriptionType === 'device') &&
            license.status === 'active'
        );

        if (hasDeviceLicense) {
            return {
                hasDeviceLicense: true,
                maxUsers: null, // Unlimited
                currentUsers: displayUsers.filter(u => u.status === 'active').length
            };
        }

        // Calculate total max users from all active user licenses
        const activeUserLicenses = licenses.filter(license =>
            (license.licenseType === '사용자구독' || license.subscriptionType === 'user') &&
            license.status === 'active'
        );

        const totalMaxUsers = activeUserLicenses.reduce((sum, license) => {
            const maxUsers = typeof license.maxUsers === 'string'
                ? (license.maxUsers === '-' ? 0 : parseInt(license.maxUsers) || 0)
                : (license.maxUsers || 0);
            return sum + maxUsers;
        }, 0);

        const currentActiveUsers = displayUsers.filter(u => u.status === 'active').length;

        return {
            hasDeviceLicense: false,
            maxUsers: totalMaxUsers,
            currentUsers: currentActiveUsers,
            availableSlots: totalMaxUsers - currentActiveUsers
        };
    }, [licenses, displayUsers, omitLicenseQuotaCheck]);

    // Validate license limit before adding users
    const validateLicenseLimit = (newUserCount: number): { valid: boolean; message?: string } => {
        if (licenseInfo.hasDeviceLicense) return { valid: true };
        if (licenseInfo.maxUsers === null || licenseInfo.maxUsers === 0) {
            return { valid: false, message: t('userList.licenseNoPurchase') };
        }
        const totalAfterAdd = licenseInfo.currentUsers + newUserCount;
        if (totalAfterAdd > licenseInfo.maxUsers) {
            return {
                valid: false,
                message: t('userList.licenseOverLimit', {
                    current: licenseInfo.currentUsers,
                    max: licenseInfo.maxUsers,
                    available: Math.max(0, licenseInfo.availableSlots),
                }),
            };
        }
        return { valid: true };
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setIsEditModalOpen(true);
    };

    const handleSaveUser = async (updatedUser: User) => {
        if (onSaveUser) {
            await onSaveUser(updatedUser);
            return;
        }
        setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)));
    };

    const handleAddMembers = (members: MockMember[]) => {
        const validation = validateLicenseLimit(members.length);
        if (!validation.valid) {
            alert(validation.message);
            return;
        }
        // API 재조회를 통해 실제 데이터를 받아옴 (임시 UI 업데이트 제거)
        if (onAddMember) {
            const newUsers: User[] = members.map((m) => ({
                id: String(m.userId),
                name: m.name,
                email: '',
                type: 'regular' as const,
                accountType: '정회원',
                customerType: '기관고객',
                role: 'Student',
                registeredDate: new Date().toISOString().slice(0, 10),
                status: 'active' as const,
                loginId: m.loginId,
                organizationName: m.organizationName,
            }));
            onAddMember(newUsers);
        }
    };

    const handleCreateGuests = async (newGuests: Partial<User>[], context: CreateGuestSaveContext) => {
        // Validate license limit
        const validation = validateLicenseLimit(newGuests.length);
        if (!validation.valid) {
            alert(validation.message);
            return;
        }

        // API 호출 (로컬 state 추가 제거 - API 성공 시 useQuery가 자동 갱신)
        if (onCreateGuest) {
            await onCreateGuest(newGuests, context);
        }
    };

    const getStatusVariant = (status: string): 'default' | 'destructive' => {
        return status === 'active' ? 'default' : 'destructive';
    };

    // Pagination logic - 백엔드 페이지네이션 사용
    const totalPages = externalTotalPages ?? Math.ceil(userStats.total / itemsPerPage);
    const paginatedUsers = displayUsers;

    const {
        clearSelectedUsers,
        handleToggleCurrentPageSelection,
        handleToggleUserSelection,
        isAllPaginatedUsersSelected,
        isSomePaginatedUsersSelected,
        selectedUserIds,
    } = useUserSelection(displayUsers, paginatedUsers);

    useEffect(() => {
        clearSelectedUsers();
    }, [initialUsers, clearSelectedUsers]);

    const handleDownload = () => {
        const list = displayUsers;
        const headers = [
            tUsers('tablePermission'),
            tUsers('tableUserId'),
            tUsers('tableUserName'),
            tUsers('tableEmail'),
            tUsers('tableAffiliation'),
            tUsers('tablePositionDepartment'),
            tUsers('tableSimulationCount'),
            tUsers('tableClassCount'),
            tUsers('tableLoginRegisteredDate'),
            tUsers('tableStatus'),
        ];
        const rows = list.map((u) => {
            const jobCol = formatUserListJobTitle(u, tAuth);
            const posDeptSecondary = u.department?.trim() || (jobCol !== '-' ? jobCol : '');
            const roleCell = formatUsersPermissionLabel(u, tUsers);
            return [
                roleCell,
                u.loginId || String(u.id),
                u.name,
                u.email?.trim() || '',
                u.affiliation?.trim() || '',
                [u.position?.trim() || '-', posDeptSecondary || '-'].join(' / '),
                u.simulationCount ?? '',
                resolveClassCountDisplay(u),
                [u.lastLogin ? formatDateTime(u.lastLogin, profileLocale) : '-', u.registeredDate ? formatDateTime(u.registeredDate, profileLocale) : '-'].join(' / '),
                u.status === 'active' ? tUsers('statusActive') : tUsers('statusInactive'),
            ];
        });
        const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = t('userList.csvFileName', { date: new Date().toISOString().slice(0, 10) });
        a.click();
        URL.revokeObjectURL(url);
    };

    const selectedDeleteCount = selectedUserIds.size;

    const handleDeleteSelectedUsers = () => {
        if (selectedDeleteCount === 0) return;
        const toDelete = displayUsers.filter((u) => selectedUserIds.has(String(u.id)));
        if (toDelete.length === 0) return;
        if (!window.confirm(t('userList.confirmDeleteSelected', { count: toDelete.length }))) return;
        const idSet = new Set(toDelete.map((u) => String(u.id)));
        setUsers((prev) => prev.filter((u) => !idSet.has(String(u.id))));
        clearSelectedUsers();
        onDeleteSelectedUsers?.(toDelete);
    };

    return (
        <>
            {showStats && (
                <div className="pt-6 my-4 flex justify-between items-center flex-wrap gap-4">
                    <div className="flex gap-6 px-2 items-center">
                        <div>
                            <span className="text-base font-medium text-gray-500 mr-4">{t('userList.master')}</span>
                            <span className="text-base font-bold text-brand-500">{userStats.masters}</span>
                        </div>
                        <div>
                            <span className="text-base font-medium text-gray-500 mr-4">{t('userList.permissionRegular')}</span>
                            <span className="text-base font-bold text-brand-500">{userStats.regularMembers}</span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="md"
                            onClick={() => setIsAddMemberModalOpen(true)}
                        >
                            <UsersIcon size={16} />
                            {t('userList.addMember')}
                        </Button>
                        <Button
                            variant="outline"
                            size="md"
                            onClick={() => setIsCreateGuestModalOpen(true)}
                        >
                            <Plus size={16} />
                            {t('userList.addGuest')}
                        </Button>
                        <Button
                            variant="lightdark"
                            size="md"
                            onClick={handleDownload}
                            aria-label={t('userList.ariaDownload')}
                        >
                            <Download size={16} />
                            <span className="hidden sm:inline">{t('userList.download')}</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="md"
                            onClick={handleDeleteSelectedUsers}
                            aria-label={t('userList.ariaDeleteSelected')}
                            disabled={selectedDeleteCount === 0}
                        >
                            <Trash2 size={16} />
                            <span className="hidden sm:inline">
                                {t('userList.deleteSelected')}
                                {selectedDeleteCount > 0 ? ` (${selectedDeleteCount})` : ''}
                            </span>
                        </Button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50/50">
                            <TableHead>
                                <Checkbox
                                    checked={isAllPaginatedUsersSelected ? true : isSomePaginatedUsersSelected ? 'indeterminate' : false}
                                    onCheckedChange={handleToggleCurrentPageSelection}
                                    aria-label={t('userList.selectAllOnPage')}
                                />
                            </TableHead>
                            <TableHead>{tUsers('tablePermission')}</TableHead>
                            <TableHead className="text-left">{tUsers('tableUserId')}</TableHead>
                            <TableHead>{tUsers('tableUserName')}</TableHead>
                            <TableHead>{tUsers('tableEmail')}</TableHead>
                            <TableHead>{tUsers('tableAffiliation')}</TableHead>
                            <TableHead>{tUsers('tablePositionDepartment')}</TableHead>
                            <TableHead>{tUsers('tableClassCount')}</TableHead>
                            <TableHead>{tUsers('tableSimulationCount')}</TableHead>
                            <TableHead>{tUsers('tableLoginRegisteredDate')}</TableHead>
                            <TableHead className="text-center">{tUsers('tableStatus')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {displayUsers.length > 0 ? (
                            paginatedUsers.map((user) => {
                                const permissionKind = getUserPermissionKind(user);
                                const badgeStyle = userListPermissionBadgeClass(permissionKind);
                                const isWithdrawal = user.status === 'withdrawal';
                                const displayName = isWithdrawal ? tUsers('deletedUser') : user.name;
                                const displayLoginId = isWithdrawal ? '-' : (user.loginId || '-');
                                const jobTitleLabel = formatUserListJobTitle(user, tAuth);
                                return (
                                    <TableRow
                                        key={user.id}
                                        className={cn(
                                            'hover:bg-gray-50 transition-colors',
                                            isWithdrawal && 'opacity-60'
                                        )}
                                    >
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedUserIds.has(String(user.id))}
                                                onCheckedChange={(checked) => handleToggleUserSelection(user.id, checked)}
                                                aria-label={t('userList.selectUserRow', { name: displayName })}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={badgeStyle.variant}
                                                className={cn('font-medium', badgeStyle.className)}
                                            >
                                                {formatUsersPermissionLabel(user, tUsers)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="min-w-[120px] text-left">
                                            <button
                                                type="button"
                                                onClick={() => handleEdit(user)}
                                                className="block w-full text-left font-medium text-brand-500 hover:text-brand-700 hover:underline"
                                                title={tUsers('edit')}
                                            >
                                                {displayLoginId}
                                            </button>
                                        </TableCell>
                                        <TableCell>
                                            <strong className="min-w-[100px] font-semibold text-gray-900">{displayName}</strong>
                                        </TableCell>
                                        <TableCell>
                                            <span className="min-w-[100px] text-gray-700">{isWithdrawal ? '-' : (user.email?.trim() || '-')}</span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="min-w-[80px]">{user.affiliation?.trim() || '-'}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <div>{user.position?.trim() || '-'}</div>
                                                <div className="text-sm text-gray-600">{jobTitleLabel}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="min-w-[48px]">{resolveClassCountDisplay(user)}</TableCell>
                                        <TableCell className="min-w-[48px]">{user.simulationCount ?? '-'}</TableCell>
                                        <TableCell>
                                            <div>
                                                <div className="text-sm text-brand-600">
                                                    {user.lastLogin ? formatDateTime(user.lastLogin, profileLocale) : '-'}
                                                </div>
                                                <div>
                                                    {user.registeredDate ? formatDateTime(user.registeredDate, profileLocale) : '-'}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge variant={getStatusVariant(user.status)}>
                                                {user.status === 'active' ? tUsers('statusActive') : tUsers('statusInactive')}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={11} className="py-16">
                                    {/* Empty State - 사용자 목록 비어 있는 상태 */}
                                    <div className="flex flex-col items-center justify-center text-center">
                                        <div className="mb-4 p-4 bg-gray-100 rounded-full">
                                            <UsersIcon size={48} className="text-gray-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                            {t('userList.noUsers')}
                                        </h3>
                                        <p className="text-sm text-gray-500 mb-6 max-w-md">
                                            {t('userList.noUsersMock')}
                                        </p>
                                        {showStats && (
                                            <div className="flex gap-3">
                                                <Button
                                                    variant="lightdark"
                                                    size="md"
                                                    onClick={() => setIsAddMemberModalOpen(true)}
                                                >
                                                    <UsersIcon size={18} />
                                                    {t('userList.addMember')}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="md"
                                                    onClick={() => setIsCreateGuestModalOpen(true)}
                                                >
                                                    <Plus size={18} />
                                                    {t('userList.createGuest')}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {displayUsers.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={userStats.total}
                    onPageChange={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                />
            )}

            <EditUserModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                user={selectedUser}
                onSave={handleSaveUser}
            />

            <AddMemberModal
                isOpen={isAddMemberModalOpen}
                onClose={() => setIsAddMemberModalOpen(false)}
                onAddMembers={handleAddMembers}
                onSuccess={() => setIsAddMemberModalOpen(false)}
                organizationId={organizationId || undefined}
                currentUsers={users}
                licenseInfo={licenseInfo}
            />

            <CreateGuestModal
                isOpen={isCreateGuestModalOpen}
                onClose={() => setIsCreateGuestModalOpen(false)}
                onSave={handleCreateGuests}
                licenseInfo={licenseInfo}
            />
        </>
    );
}
